import { SYSTEM_PROMPT_MAX_LENGTH, type GenerationInstructionsResource } from '@studio/shared';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { Failure } from '../../shell/EmptyState.js';
import { useAsync } from '../../shell/useAsync.js';
import './styles.css';

/** The editor stays on the current page so opening it never discards an unfinished Brief. */
export function GenerationInstructionsSettings(): ReactNode {
  const [opened, setOpened] = useState(false);
  return <details className="generation-instructions" onToggle={(event) => { if (event.currentTarget.open) setOpened(true); }}>
    <summary>Системный промпт генерации</summary>
    {opened ? <Instructions /> : null}
  </details>;
}

function Instructions(): ReactNode {
  const loaded = useAsync(() => api.readGenerationInstructions(), []);
  const [notice, setNotice] = useState<string | null>(null);
  return <div className="generation-instructions-content">
    <p className="note">Общие инструкции для всех приложений: роль дизайнера, постоянные требования к стилю и качеству.
      Текст добавляется перед заданием при создании новых вариантов и доработок.</p>
    <p className="note">Повтор незавершённого варианта использует прежние сохранённые инструкции. Пустой промпт отключает общие инструкции.</p>
    {loaded.loading ? <p role="status">Загружаю системный промпт…</p> : loaded.error !== null || loaded.value === null
      ? <Failure message={loaded.error ?? 'Системный промпт не удалось прочитать.'} onRetry={loaded.reload} />
      : <InstructionsForm key={loaded.value.version} saved={loaded.value} onReload={() => { setNotice(null); loaded.reload(); }}
        onEdit={() => setNotice(null)}
        onSaved={(next) => { loaded.set(next); setNotice('Системный промпт сохранён. Новые варианты и доработки получат эти инструкции.'); }} />}
    {notice === null ? null : <p className="generation-instructions-saved" role="status">{notice}</p>}
  </div>;
}

function InstructionsForm({ saved, onSaved, onReload, onEdit }: {
  saved: GenerationInstructionsResource; onSaved: (next: GenerationInstructionsResource) => void; onReload: () => void; onEdit: () => void;
}): ReactNode {
  const [draft, setDraft] = useState(saved.systemPrompt);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setBusy(true); setFailure(null);
    try { onSaved(await api.saveGenerationInstructions({ systemPrompt: draft, expectedVersion: saved.version })); }
    catch (caught) { setFailure(caught instanceof ApiError ? caught.message : 'Не удалось сохранить системный промпт. Попробуйте снова.'); }
    finally { setBusy(false); }
  };
  return <form className="generation-instructions-form" onSubmit={(event) => void submit(event)}>
    <label htmlFor="generation-system-prompt">Системный промпт</label>
    <textarea id="generation-system-prompt" rows={7} maxLength={SYSTEM_PROMPT_MAX_LENGTH} value={draft} disabled={busy}
      onChange={(event) => { setDraft(event.target.value); onEdit(); }}
      placeholder="Ты опытный дизайнер оформления Google Play. Создавай выразительные композиции с читаемыми заголовками. Сохраняй единый визуальный стиль и узнаваемость приложения." />
    {failure === null ? null : <><Failure message={failure} /><button type="button" className="secondary" disabled={busy} onClick={onReload}>Загрузить сохранённый промпт</button></>}
    <div className="row-actions"><button className="primary" disabled={busy || draft === saved.systemPrompt}>
      {busy ? 'Сохраняю промпт…' : 'Сохранить системный промпт'}</button>
      <button className="ghost" type="button" disabled={busy || draft === saved.systemPrompt} onClick={() => { setDraft(saved.systemPrompt); setFailure(null); }}>Отменить изменения</button></div>
  </form>;
}
